import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, CreditCard, Loader2, X, Calendar, Layers, RotateCcw } from 'lucide-react';
import {
    fetchCreditCardOptions,
    createCreditCardOption,
    updateCreditCardOption,
    deleteCreditCardOption
} from '../api/creditCardOptionsApi';

const AdminPaymentMethods = () => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [newCard, setNewCard] = useState({
        type: '',
        bank: '',
        installmentsNoInterest: [],
        discount: 0,
        specialRules: []
    });

    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkInstallments, setBulkInstallments] = useState([]);
    const [selectedBanks, setSelectedBanks] = useState([]);
    const [selectedTypes, setSelectedTypes] = useState([]);

    const daysOfWeek = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

    // Available installment options
    const availableInstallments = [1, 3, 6, 9, 10, 12, 15, 18, 24];

    useEffect(() => {
        loadOptions();
    }, []);

    const loadOptions = async () => {
        try {
            setLoading(true);
            const data = await fetchCreditCardOptions();
            setOptions(data);
        } catch (error) {
            console.error('Error loading credit card options:', error);
            alert('Error al cargar los medios de pago');
        } finally {
            setLoading(false);
        }
    };

    const handleInstallmentToggle = (optionId, installment) => {
        setOptions(prev => prev.map(option => {
            if (option.id === optionId) {
                const currentInstallments = option.installmentsNoInterest || [];
                const index = currentInstallments.indexOf(installment);

                let newInstallments;
                if (index > -1) {
                    newInstallments = currentInstallments.filter(i => i !== installment);
                } else {
                    newInstallments = [...currentInstallments, installment].sort((a, b) => a - b);
                }

                return { ...option, installmentsNoInterest: newInstallments };
            }
            return option;
        }));
    };

    const handleNewCardInstallmentToggle = (installment) => {
        setNewCard(prev => {
            const currentInstallments = prev.installmentsNoInterest || [];
            const index = currentInstallments.indexOf(installment);

            let newInstallments;
            if (index > -1) {
                newInstallments = currentInstallments.filter(i => i !== installment);
            } else {
                newInstallments = [...currentInstallments, installment].sort((a, b) => a - b);
            }

            return { ...prev, installmentsNoInterest: newInstallments };
        });
    };

    const handleAddRule = () => {
        setNewCard(prev => ({
            ...prev,
            specialRules: [
                ...(prev.specialRules || []),
                { days: [], installments: [] }
            ]
        }));
    };

    const handleRemoveRule = (index) => {
        setNewCard(prev => ({
            ...prev,
            specialRules: prev.specialRules.filter((_, i) => i !== index)
        }));
    };

    const handleRuleDayToggle = (ruleIndex, dayIndex) => {
        setNewCard(prev => {
            const newRules = [...(prev.specialRules || [])];
            const rule = { ...newRules[ruleIndex] };
            const currentDays = rule.days || [];

            if (currentDays.includes(dayIndex)) {
                rule.days = currentDays.filter(d => d !== dayIndex);
            } else {
                rule.days = [...currentDays, dayIndex].sort();
            }

            newRules[ruleIndex] = rule;
            return { ...prev, specialRules: newRules };
        });
    };

    const handleRuleInstallmentToggle = (ruleIndex, installment) => {
        setNewCard(prev => {
            const newRules = [...(prev.specialRules || [])];
            const rule = { ...newRules[ruleIndex] };
            const currentInstallments = rule.installments || [];

            if (currentInstallments.includes(installment)) {
                rule.installments = currentInstallments.filter(i => i !== installment);
            } else {
                rule.installments = [...currentInstallments, installment].sort((a, b) => a - b);
            }

            newRules[ruleIndex] = rule;
            return { ...prev, specialRules: newRules };
        });
    };

    const openModal = (card = null) => {
        if (card) {
            setNewCard({
                id: card.id,
                type: card.type,
                bank: card.bank,
                installmentsNoInterest: card.installmentsNoInterest || [],
                discount: card.discount || 0,
                specialRules: card.specialRules || [] // Ensure specialRules is carried over
            });
        } else {
            setNewCard({
                type: '',
                bank: '',
                installmentsNoInterest: [],
                discount: 0,
                specialRules: []
            });
        }
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setNewCard({
            type: '',
            bank: '',
            installmentsNoInterest: [],
            discount: 0,
            specialRules: []
        });
    };

    const handleSaveCard = async (e) => {
        e.preventDefault();

        if (!newCard.type.trim() || !newCard.bank.trim()) {
            alert('Por favor complete todos los campos');
            return;
        }

        try {
            if (newCard.id) {
                // Editing existing card - update local state only (will be saved with "Guardar Cambios")
                setOptions(prev => prev.map(o => o.id === newCard.id ? newCard : o));
                closeModal();
            } else {
                // Creating new card - save to API immediately
                const created = await createCreditCardOption(newCard);
                setOptions(prev => [...prev, created]);
                closeModal();
            }
        } catch (error) {
            console.error('Error saving card:', error);
            alert('Error al guardar la tarjeta');
        }
    };

    const deleteOption = async (option) => {
        if (!window.confirm(`¿Estás seguro de eliminar ${option.type} - ${option.bank}?`)) {
            return;
        }

        try {
            if (option.id) {
                await deleteCreditCardOption(option.id);
            }
            setOptions(prev => prev.filter(o => o.id !== option.id));
        } catch (error) {
            console.error('Error deleting option:', error);
            alert('Error al eliminar la opción');
        }
    };

    const handleSaveAll = async () => {
        setSaving(true);
        try {
            const promises = options.map(option => {
                if (option.id) {
                    // Only send the fields that the backend expects
                    const payload = {
                        type: option.type,
                        bank: option.bank,
                        installmentsNoInterest: option.installmentsNoInterest || [],
                        discount: option.discount || 0,
                        specialRules: option.specialRules || []
                    };
                    return updateCreditCardOption(option.id, payload);
                }
                return Promise.resolve(option);
            });

            await Promise.all(promises);
            alert('Medios de pago guardados correctamente');
            await loadOptions();
        } catch (error) {
            console.error('Error saving payment methods:', error);
            alert('Error al guardar los medios de pago');
        } finally {
            setSaving(false);
        }
    };

    // Nuevo ciclo: destilda todas las cuotas de todos los bancos/tarjetas para
    // volver a cargar las promociones del mes desde cero.
    const handleNewCycle = () => {
        const checkedCount = options.reduce(
            (total, option) => total + (option.installmentsNoInterest || []).length,
            0
        );

        if (checkedCount === 0) {
            alert('No hay cuotas tildadas para desactivar');
            return;
        }

        if (!window.confirm(
            `Nuevo ciclo: se van a destildar ${checkedCount} cuotas en todos los bancos y tarjetas. ` +
            'Los reintegros y las reglas especiales se mantienen. ¿Continuar?'
        )) {
            return;
        }

        setOptions(prev => prev.map(option => ({ ...option, installmentsNoInterest: [] })));
    };

    const handleBulkApply = () => {
        if (selectedBanks.length === 0 || selectedTypes.length === 0 || bulkInstallments.length === 0) {
            alert('Selecciona al menos un banco, un tipo de tarjeta y una cuota');
            return;
        }

        setOptions(prev => prev.map(option => {
            const matchesBank = selectedBanks.includes(option.bank);
            const matchesType = selectedTypes.includes(option.type);

            if (matchesBank && matchesType) {
                const currentInstallments = option.installmentsNoInterest || [];
                // Combine and unique
                const newInstallments = [...new Set([...currentInstallments, ...bulkInstallments])].sort((a, b) => a - b);
                return { ...option, installmentsNoInterest: newInstallments };
            }
            return option;
        }));

        setShowBulkModal(false);
        setBulkInstallments([]);
        setSelectedBanks([]);
        setSelectedTypes([]);
    };

    // Group options by bank and sort
    const groupedOptions = options.reduce((acc, option) => {
        const bank = option.bank || 'Sin banco';
        if (!acc[bank]) {
            acc[bank] = [];
        }
        acc[bank].push(option);
        return acc;
    }, {});

    // Sort banks alphabetically
    const sortedBanks = Object.keys(groupedOptions).sort((a, b) => a.localeCompare(b));

    // Card priority sorting function
    const sortCardsByType = (cards) => {
        const priority = { 'visa': 1, 'master': 2, 'amex': 3 };
        return [...cards].sort((a, b) => {
            const typeA = a.type?.toLowerCase() || '';
            const typeB = b.type?.toLowerCase() || '';

            const getRank = (type) => {
                if (type.includes('visa')) return priority['visa'];
                if (type.includes('master')) return priority['master'];
                if (type.includes('amex') || type.includes('american')) return priority['amex'];
                return 99;
            };

            const rankA = getRank(typeA);
            const rankB = getRank(typeB);

            if (rankA !== rankB) return rankA - rankB;
            return typeA.localeCompare(typeB);
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Medios de Pago</h1>
                    <p className="text-gray-500 mt-1">Gestiona tarjetas, bancos y cuotas disponibles</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => openModal()}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                        <Plus className="w-5 h-5" />
                        Agregar Tarjeta
                    </button>
                    <button
                        onClick={() => {
                            setSelectedBanks([]);
                            setSelectedTypes([]);
                            setBulkInstallments([]);
                            setShowBulkModal(true);
                        }}
                        className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
                    >
                        <Layers className="w-5 h-5" />
                        Acción Masiva
                    </button>
                    <button
                        onClick={handleNewCycle}
                        className="bg-red-50 text-red-700 border border-red-200 px-4 py-2 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2 font-medium"
                        title="Destilda todas las cuotas de todos los bancos y tarjetas"
                    >
                        <RotateCcw className="w-5 h-5" />
                        Nuevo Ciclo
                    </button>
                    <button
                        onClick={handleSaveAll}
                        disabled={saving}
                        className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Guardando...
                            </>
                        ) : (
                            <>
                                <Save className="w-5 h-5" />
                                Guardar Cambios
                            </>
                        )}
                    </button>
                </div>
            </div>

            <div className="space-y-6">
                {sortedBanks.map(bank => (
                    <div key={bank} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                            <div className="flex items-center gap-3">
                                <CreditCard className="w-6 h-6 text-purple-600" />
                                <h2 className="text-xl font-bold text-gray-900 capitalize">{bank}</h2>
                            </div>
                        </div>

                        <div className="p-6 space-y-6">
                            {sortCardsByType(groupedOptions[bank]).map((option, index) => (
                                <div key={option.id || index} className="border-b border-gray-100 pb-6 last:border-0 last:pb-0">
                                    <div className="flex justify-between items-center mb-4">
                                        <div className="flex items-center gap-4 flex-1">
                                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                                <span className={`px-3 py-1 rounded-full text-sm ${option.type?.toLowerCase().includes('visa')
                                                    ? 'bg-blue-100 text-blue-800'
                                                    : option.type?.toLowerCase().includes('master')
                                                        ? 'bg-orange-100 text-orange-800'
                                                        : 'bg-purple-100 text-purple-800'
                                                    }`}>
                                                    {option.type}
                                                </span>
                                            </h3>
                                            <div className="flex items-center gap-2">
                                                <label className="text-sm text-gray-600 font-medium">Reintegro:</label>
                                                <div className="relative w-24">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        className="w-full px-2 py-1 rounded border border-gray-300 text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                                                        value={option.discount || ''}
                                                        onChange={(e) => {
                                                            const val = parseInt(e.target.value) || 0;
                                                            setOptions(prev => prev.map(o =>
                                                                o.id === option.id ? { ...o, discount: val } : o
                                                            ));
                                                        }}
                                                    />
                                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">%</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => openModal(option)}
                                                className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                                                title="Editar tarjeta"
                                            >
                                                <Save className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={() => deleteOption(option)}
                                                className="text-red-600 hover:bg-red-50 p-2 rounded-lg transition-colors"
                                                title="Eliminar tarjeta"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-4 md:grid-cols-10 gap-3">
                                        {availableInstallments.map(installment => (
                                            <label
                                                key={installment}
                                                className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-3 rounded-lg border border-gray-200 transition-colors"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={option.installmentsNoInterest?.includes(installment) || false}
                                                    onChange={() => handleInstallmentToggle(option.id, installment)}
                                                    className="w-4 h-4 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                                                />
                                                <span className="font-medium text-gray-700 text-sm">
                                                    {installment}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {options.length === 0 && (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                    <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">No hay tarjetas configuradas</p>
                    <button
                        onClick={() => openModal()}
                        className="mt-4 text-purple-600 hover:text-purple-700 font-medium"
                    >
                        Agregar primera tarjeta
                    </button>
                </div>
            )}

            {/* Add Card Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                            <h2 className="text-2xl font-bold text-gray-900">
                                {newCard.id ? 'Editar Tarjeta' : 'Agregar Tarjeta'}
                            </h2>
                            <button
                                onClick={closeModal}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSaveCard} className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Tipo de Tarjeta *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="ej: Visa, MasterCard, Naranja"
                                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                        value={newCard.type}
                                        onChange={(e) => setNewCard(prev => ({ ...prev, type: e.target.value }))}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Banco Emisor *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="ej: ICBC, Nación, Santander"
                                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                        value={newCard.bank}
                                        onChange={(e) => setNewCard(prev => ({ ...prev, bank: e.target.value }))}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Reintegro (%)
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        placeholder="ej: 10, 15, 20"
                                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                        value={newCard.discount || ''}
                                        onChange={(e) => setNewCard(prev => ({ ...prev, discount: parseInt(e.target.value) || 0 }))}
                                    />
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label className="block text-sm font-medium text-gray-700">
                                    Cuotas sin Interés
                                </label>
                                <div className="grid grid-cols-4 md:grid-cols-7 gap-3">
                                    {availableInstallments.map(installment => (
                                        <label
                                            key={installment}
                                            className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-3 rounded-lg border border-gray-200 transition-colors"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={newCard.installmentsNoInterest?.includes(installment) || false}
                                                onChange={() => handleNewCardInstallmentToggle(installment)}
                                                className="w-4 h-4 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                                            />
                                            <span className="font-medium text-gray-700 text-sm">
                                                {installment}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-3 pt-4 border-t border-gray-100">
                                <div className="flex justify-between items-center">
                                    <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-purple-600" />
                                        Promociones por Días
                                    </label>
                                    <button
                                        type="button"
                                        onClick={handleAddRule}
                                        className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Agregar Regla
                                    </button>
                                </div>

                                {newCard.specialRules?.map((rule, ruleIndex) => (
                                    <div key={ruleIndex} className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-3 relative">
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveRule(ruleIndex)}
                                            className="absolute top-2 right-2 text-gray-400 hover:text-red-500 transition-colors"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>

                                        <div>
                                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Días Activos</span>
                                            <div className="flex gap-2 flex-wrap">
                                                {daysOfWeek.map((day, dayIndex) => (
                                                    <button
                                                        key={day}
                                                        type="button"
                                                        onClick={() => handleRuleDayToggle(ruleIndex, dayIndex)}
                                                        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${rule.days?.includes(dayIndex)
                                                            ? 'bg-purple-600 text-white'
                                                            : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                                                            }`}
                                                    >
                                                        {day}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Cuotas Adicionales</span>
                                            <div className="flex gap-2 flex-wrap">
                                                {availableInstallments.map(installment => (
                                                    <label
                                                        key={installment}
                                                        className={`flex items-center gap-1 px-2 py-1 rounded border cursor-pointer transition-colors ${rule.installments?.includes(installment)
                                                            ? 'bg-purple-50 border-purple-200 text-purple-700'
                                                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            className="hidden"
                                                            checked={rule.installments?.includes(installment) || false}
                                                            onChange={() => handleRuleInstallmentToggle(ruleIndex, installment)}
                                                        />
                                                        <span className="text-xs font-medium">{installment}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {(!newCard.specialRules || newCard.specialRules.length === 0) && (
                                    <p className="text-sm text-gray-500 italic">No hay reglas especiales configuradas.</p>
                                )}
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 font-medium transition-colors flex items-center gap-2"
                                >
                                    {newCard.id ? <Save className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                                    {newCard.id ? 'Guardar Cambios' : 'Agregar Tarjeta'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {/* Bulk Add Modal */}
            {showBulkModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
                        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <Layers className="w-5 h-5 text-orange-500" />
                                Agregar Cuotas Masivo
                            </h2>
                            <button onClick={() => setShowBulkModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    1. Selecciona los Bancos
                                </label>
                                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border border-gray-100 rounded-lg">
                                    {[...new Set(options.map(o => o.bank))].filter(Boolean).sort((a, b) => a.localeCompare(b)).map(bank => (
                                        <label key={bank} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={selectedBanks.includes(bank)}
                                                onChange={() => {
                                                    setSelectedBanks(prev => prev.includes(bank) ? prev.filter(b => b !== bank) : [...prev, bank]);
                                                }}
                                                className="w-4 h-4 text-orange-500 rounded focus:ring-orange-400"
                                            />
                                            <span className="text-sm text-gray-700 truncate capitalize">{bank}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    2. Selecciona el Tipo de Tarjeta
                                </label>
                                <div className="grid grid-cols-2 gap-2 p-2 border border-gray-100 rounded-lg">
                                    {[...new Set(options.map(o => o.type))].filter(Boolean).sort((a, b) => a.localeCompare(b)).map(type => (
                                        <label key={type} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={selectedTypes.includes(type)}
                                                onChange={() => {
                                                    setSelectedTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
                                                }}
                                                className="w-4 h-4 text-orange-500 rounded focus:ring-orange-400"
                                            />
                                            <span className="text-sm text-gray-700 truncate">{type}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    3. Selecciona las Cuotas a Agregar
                                </label>
                                <div className="grid grid-cols-5 gap-2">
                                    {availableInstallments.map(installment => (
                                        <label key={installment} className="flex flex-col items-center gap-1 p-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-orange-50 transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={bulkInstallments.includes(installment)}
                                                onChange={() => {
                                                    setBulkInstallments(prev => prev.includes(installment) ? prev.filter(i => i !== installment) : [...prev, installment]);
                                                }}
                                                className="w-4 h-4 text-orange-500 rounded focus:ring-orange-400"
                                            />
                                            <span className="text-xs font-bold text-gray-700">{installment}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-orange-50 p-4 rounded-lg flex gap-3 text-orange-700 text-sm">
                                <Layers className="w-5 h-5 shrink-0" />
                                <p>Esta acción agregará las cuotas seleccionadas a todas las tarjetas de los bancos marcados.
                                    <br /><strong>Nota:</strong> Deberás presionar "Guardar Cambios" para confirmar.</p>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={() => setShowBulkModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleBulkApply}
                                    className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-bold transition-colors shadow-md shadow-orange-200"
                                >
                                    Aplicar a {selectedBanks.length} bancos
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPaymentMethods;
